import React, { useEffect, useReducer } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import 'react-datepicker/dist/react-datepicker.css'; // base; themed in theme.css
import 'leaflet/dist/leaflet.css'; // base; themed in theme.css
// Self-hosted variable fonts, used app-wide (Fraunces display serif + Inter body/UI).
// Bundled locally — no external font request, works offline / behind a proxy. Both register
// under the family names 'Fraunces Variable' / 'Inter Variable'; the --font-* tokens in
// styles/theme.css must spell them exactly or the browser silently uses the system font.
import '@fontsource-variable/fraunces';
import '@fontsource-variable/inter';
// Real italics for Inter. The default entry above ships upright faces ONLY, so every `italic`
// in the app was a browser-synthesised oblique — an upright glyph mechanically sheared, which
// renders visibly rough. With the true italic loaded, `font-synthesis-style: none` (index.css)
// is safe and italics are drawn, not faked. The browser fetches this only on pages that
// actually render italic text.
import '@fontsource-variable/inter/wght-italic.css';
import './styles/theme.css'; // Tailwind v4 entry + Green Bay design tokens (loaded last)
import './lib/time'; // installs dayjs IST default — must be imported before any date rendering
import App from './App';
import { useThemeStore } from './store/theme';
import { Toaster } from './ui';

/**
 * THE COUNTERS THE WHOLE APP SHARES — refreshed from one place, after every write.
 *
 * The navbar badges, the "My Work" list, the approvals hub and the notification bell are four
 * different reads of the SAME question: what is still waiting on you? They are fetched separately
 * (and polled on a 45–60s timer), so after an approval the four disagreed for up to a minute —
 * the row vanished from the hub while the badge beside it still counted it, which reads as a bug
 * even though every number was individually correct.
 *
 * Invalidating them here, in the mutation cache, rather than in each screen is the point: it holds
 * for every mutation in the app, including the ones written next year and the ones whose authors
 * never think about the navbar. Screens that also invalidate these keys themselves are harmless —
 * React Query coalesces, and an inactive query is only marked stale, not refetched.
 */
const WORK_COUNTER_KEYS = [
  ['badges'],            // navbar counts
  ['worklist'],          // "My Work" (and the once-a-day greeting)
  ['approvals'],         // the hub's queue, lane tiles and decided list
  ['notifications-feed'], // the bell — writes retire read receipts server-side
];

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      // A mutation may opt out (`meta.skipWorkRefresh`) when it is a high-frequency write that
      // cannot change anybody's queue — a draft autosave, say.
      if (mutation.meta?.skipWorkRefresh) return;
      for (const queryKey of WORK_COUNTER_KEYS) void queryClient.invalidateQueries({ queryKey });
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function ThemedApp() {
  const mode = useThemeStore((s) => s.mode);
  const resolved = useThemeStore((s) => s.resolved)();
  const [, forceRerender] = useReducer((x: number) => x + 1, 0);

  // Re-render when the OS scheme flips and the user has 'system' selected.
  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    // On a live OS light↔dark switch, `mode` is still 'system' (unchanged) and the store exposes
    // `resolved` as a STABLE function, so `setState({mode:'system'})` changes no snapshot → no re-render
    // → data-theme would stay stale. Re-apply data-theme directly (drives all CSS) AND force a re-render
    // so any JS consumer of resolved() refreshes too.
    const handler = () => {
      document.documentElement.setAttribute('data-theme', useThemeStore.getState().resolved());
      forceRerender();
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [mode]);

  // Sync the html data-theme attribute so our CSS can target it. The page background/text then flip
  // via the token (`body { background: var(--gb-canvas) }` in index.css) — do NOT set an inline
  // body background here: an inline style overrides the token and freezes the colour to one theme.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  // The app is fully Tailwind now (Ant Design removed) — theming lives in styles/theme.css,
  // driven by the `data-theme` attribute synced by the effect above; no provider wrapper needed.
  return (
    <>
      <App />
      <Toaster />
    </>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
